
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  feedback: true,
  toc: true,
  layout: 'guide',
  description: 'Learn how to track payment consent status and allow users to revoke consent'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Managing and revoking consent' subtitle='Learn how to track payment consent status and allow users to revoke consent' mdxType="Header" />
    <h4 {...{
      "id": "tracking-consent-status"
    }}>{`Tracking consent status`}</h4>
    <p>{`Whenever a consent status changes, Plaid will send a `}<a parentName="p" {...{
        "href": "/docs/api/products/payment-initiation/#consent_status_update"
      }}><inlineCode parentName="a">{`CONSENT_STATUS_UPDATE`}</inlineCode></a>{` webhook to the webhook listener endpoint that you specified during the `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{` call. `}</p>
    <p>{`You can also retrieve consent status using `}<inlineCode parentName="p">{`/payment_initiation/consent/get`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "consent-authorisation"
    }}>{`Consent authorisation`}</h4>
    <p>{`All consents begin in the `}<inlineCode parentName="p">{`UNAUTHORISED`}</inlineCode>{` status.`}</p>
    <p>{`Once a user has `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/variable-recurring-payments/add-to-app/#launch-the-payment-flow-in-link"
      }}>{`completed the consent flow in Link`}</a>{`, the consent status will update to `}<inlineCode parentName="p">{`AUTHORISED`}</inlineCode>{`.`}</p>
    <p>{`At this point you can make payments within the consent parameters, with no user input required. `}</p>
    <p>{`If the user exits Link without authorising the payment, or rejects the payment within Link, the consent will still remain in the `}<inlineCode parentName="p">{`UNAUTHORISED`}</inlineCode>{` status. `}</p>
    <h4 {...{
      "id": "consent-revocation"
    }}>{`Consent revocation`}</h4>
    <p>{`The end user may decide to revoke their consent, which can be done via their bank, your app or the `}<a parentName="p" {...{
        "href": "https://my.plaid.com"
      }}>{`Plaid Portal`}</a>{`. After consent has been revoked, the consent status will be updated to `}<inlineCode parentName="p">{`REVOKED`}</inlineCode>{`, and the `}<inlineCode parentName="p">{`consent_id`}</inlineCode>{` can no longer be used to make payments. There is no way to restore a revoked `}<inlineCode parentName="p">{`consent_id`}</inlineCode>{`; you will need to create a new `}<inlineCode parentName="p">{`consent_id`}</inlineCode>{` and send the user back through Link to grant consent.`}</p>
    <p>{`To allow end users to revoke consent via your app, implement the `}<inlineCode parentName="p">{`/payment_initiation/consent/revoke`}</inlineCode>{` endpoint and create an entry point for it in your UI. `}</p>
    <MDXCodeBlock type='server' title='/payment_initiation/consent/revoke request' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = PaymentInitiationConsentRevokeRequest(consent_id=consent_id)

response = client.payment_initiation_consent_revoke(request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewPaymentInitiationConsentRevokeRequest(consentID)

response, _, err := client.PlaidApi.PaymentInitiationConsentRevoke(ctx).PaymentInitiationConsentRevokeRequest(*request).Execute()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.PaymentInitiationConsentRevokeRequest{ConsentID: consentID}

response, err := client.PaymentInitiation.RevokeConsent(ctx, request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/payment_initiation/consent/revoke \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "consent_id": String
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::PaymentInitiationConsentRevokeRequest.new({ consent_id: consent_id })

response = client.payment_initiation_consent_revoke(request)
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`PaymentInitiationConsentRevokeRequest request = new PaymentInitiationConsentRevokeRequest()
  .consentId(consentId);

Response<PaymentInitiationConsentRevokeResponse> response = client
  .paymentInitiationConsentRevoke(request)
  .execute();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: PaymentInitiationConsentRevokeRequest = {
  consent_id: consentID,
};
try {
  const response = await plaidClient.paymentInitiationConsentRevoke(request);
} catch (error) {
  // handle error
}
`}</code></pre>
    </MDXCodeBlock>
    <h4 {...{
      "id": "consent-rejection"
    }}>{`Consent rejection`}</h4>
    <p>{`A consent will enter the `}<inlineCode parentName="p">{`REJECTED`}</inlineCode>{` status only if it is rejected by the bank. Common reasons may include the bank not supporting the scope of the VRP; for example, not all banks support `}<inlineCode parentName="p">{`COMMERCIAL`}</inlineCode>{` scopes.`}</p>
    <h4 {...{
      "id": "consent-expiration"
    }}>{`Consent expiration`}</h4>
    <p>{`If the consent was created with an expiration date-time when calling `}<inlineCode parentName="p">{`/payment_initiation/consent/create`}</inlineCode>{`, the consent will move into the `}<inlineCode parentName="p">{`EXPIRED`}</inlineCode>{` status once that date-time has passed. An expired consent cannot be refreshed; you must create a new one instead.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "tracking-consent-status",
        level: 4,
        title: "Tracking consent status",
      },
{
        id: "consent-authorisation",
        level: 4,
        title: "Consent authorisation",
      },
{
        id: "consent-revocation",
        level: 4,
        title: "Consent revocation",
      },
{
        id: "consent-rejection",
        level: 4,
        title: "Consent rejection",
      },
{
        id: "consent-expiration",
        level: 4,
        title: "Consent expiration",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
